function hexToRgb(hex: string) {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

function getRelativeLuminance(rgb: any) {
  const sRGB = [rgb.r, rgb.g, rgb.b].map(val => {
    val /= 255; // Normalize to 0-1
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  // Luminance formula for sRGB
  return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
}

// Only a colour both light and muted is used as it is; a strong or vivid
// one (gold, pure yellow) is washed towards white to sit behind text.
const SURFACE_MIN_LUMINANCE = 0.8;
const SURFACE_MAX_CHROMA = 64;
const SURFACE_TINT_STRENGTH = 0.2;

export function toSurfaceTint(hexColor: string) {
  const rgb = hexColor ? hexToRgb(hexColor) : null;
  if (!rgb) {
    return hexColor;
  }
  const chroma = Math.max(rgb.r, rgb.g, rgb.b) - Math.min(rgb.r, rgb.g, rgb.b);
  if (
    getRelativeLuminance(rgb) >= SURFACE_MIN_LUMINANCE &&
    chroma <= SURFACE_MAX_CHROMA
  ) {
    return hexColor;
  }
  const mix = (channel: number) =>
    Math.round(
      channel * SURFACE_TINT_STRENGTH + 255 * (1 - SURFACE_TINT_STRENGTH)
    )
      .toString(16)
      .padStart(2, "0");

  return `#${mix(rgb.r)}${mix(rgb.g)}${mix(rgb.b)}`.toUpperCase();
}

export function getContrastTextColor(hexColor: string) {
  if (hexColor === undefined) {
    return "black";
  }
  const rgb = hexToRgb(hexColor);

  if (!rgb) {
    return "black";
  }

  const luminance = getRelativeLuminance(rgb);

  // A common threshold for luminance is 0.179 or 0.5.
  // 0.179 is derived from WCAG recommendations for contrast ratio.
  // If the luminance is above this threshold, the color is considered "light" and needs dark text.
  // Otherwise, it's "dark" and needs light text.
  const threshold = 0.179; // Or 0.5 for a simpler, less accurate check

  return luminance > threshold ? "black" : "white";
}
